import React, { useState, useEffect, useMemo } from 'react'
import { KTSVG } from '../../../../_metronic/helpers'
import { toast, ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import jsPDF from 'jspdf'
import 'jspdf-autotable'
import { getVendorDetails, getVendors, getVendorCategories } from '../core/_requests'
import type { Vendor, VendorCategory } from '../core/_models'
import { useAuth } from '../../auth'
import AddVendorModal from './AddVendorModal'
import BulkVendorUploadModal from './BulkVendorUploadModal'
import RegionLibraryModal from './RegionLibraryModal'
import VendorPerformanceModal from './VendorPerformanceModal'
import { regionGates, vendorGates } from '../core/adminPermissions'
import { actorCanPickAnyCompanyGroup, isActorSuperAdmin } from '../core/adminActorContext'
import { useGlobalFilter } from '../../../shared/context/GlobalFilterContext'
import {
  adminListEmptyMessage,
  initialAdminListAccessState,
  resolveAdminListFetchError,
  type AdminListAccessState,
} from '../core/adminListAccess'
import SearchableSelect from '../../../shared/components/SearchableSelect'
import { showErrorToast } from '../../../shared/utils/apiErrorMessage'

interface VendorRow {
  id: number
  vendorCompanyMappingId?: number | null
  code: string
  vendorInitials?: string
  name: string
  email: string
  password: string | null
  contactNo: string
  altContactNo: string
  contactPerson?: string
  tag: string
  categoryIds: number[]
  companyMappings?: any[]
  totalOrders: number
  avgDeliveryDays: number
  issueCount: number
  deliveredRecords?: number
  performanceRating?: number | null
  categoryPerformance?: Vendor['categoryPerformance']
  cgaid?: number | null
  caId?: number | null
  companyGroupName?: string
  companyName?: string
}

const VendorManagement = () => {
  const [vendors, setVendors] = useState<VendorRow[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const { currentUser, isSuperAdmin, hasPermission } = useAuth()
  const { state: globalState } = useGlobalFilter()

  const showCompanyColumn = useMemo(
    () => isActorSuperAdmin(currentUser) || actorCanPickAnyCompanyGroup(currentUser),
    [currentUser]
  )
  const actionGates = useMemo(
    () => vendorGates(hasPermission, isSuperAdmin),
    [hasPermission, isSuperAdmin]
  )
  const canExportVendors = isSuperAdmin || hasPermission('EXPORT_VENDOR')
  const regionActionGates = useMemo(
    () => regionGates(hasPermission, isSuperAdmin),
    [hasPermission, isSuperAdmin]
  )
  const [listAccess, setListAccess] = useState<AdminListAccessState>(initialAdminListAccessState)

  const [showAddVendorModal, setShowAddVendorModal] = useState(false)
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false)
  const [showRegionLibraryModal, setShowRegionLibraryModal] = useState(false)
  const [editingVendor, setEditingVendor] = useState<Vendor | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [sortConfig, setSortConfig] = useState<{
    key: keyof VendorRow | null
    direction: 'asc' | 'desc'
  }>({ key: null, direction: 'asc' })
  const [vendorCategories, setVendorCategories] = useState<VendorCategory[]>([])
  const [copiedPasswords, setCopiedPasswords] = useState<Set<number>>(new Set())
  const [performanceVendor, setPerformanceVendor] = useState<Vendor | null>(null)

  const loadVendors = async () => {
    if (!actionGates.canRead) {
      setListAccess({ loading: false, accessDenied: true, loadError: null })
      setVendors([])
      return
    }
    setLoading(true)
    setListAccess((s) => ({ ...s, loading: true, accessDenied: false, loadError: null }))
    try {
      const vendorList = await getVendors()

      const showCompanyCol = showCompanyColumn
      const grouped = new Map<number, VendorRow>()
      vendorList.forEach((v) => {
        const existing = grouped.get(v.id)
        const mapping = {
          id: v.vendorCompanyMappingId ?? null,
          companyGroupName: v.companyGroupName,
          companyName: v.companyName,
          categoryIds: v.categoryIds,
          cgaid: v.cgaid,
          caId: v.caId,
        }
        if (existing) {
          existing.companyMappings = [...(existing.companyMappings || []), mapping]
          existing.categoryIds = Array.from(new Set([...(existing.categoryIds || []), ...(v.categoryIds || [])]))
          return
        }
        grouped.set(v.id, {
          id: v.id,
          vendorCompanyMappingId: v.vendorCompanyMappingId ?? null,
          code: v.code,
          vendorInitials: v.vendorInitials,
          name: v.name,
          email: v.email,
          password: v.password,
          contactNo: v.contactNo,
          altContactNo: v.altContactNo,
          contactPerson: v.contactPerson,
          tag: v.tag,
          categoryIds: v.categoryIds || [],
          companyMappings: [mapping],
          totalOrders: v.totalOrders,
          avgDeliveryDays: v.avgDeliveryDays,
          issueCount: v.issueCount,
          deliveredRecords: v.deliveredRecords,
          performanceRating: v.performanceRating,
          categoryPerformance: v.categoryPerformance,
          cgaid: v.cgaid ?? null,
          caId: v.caId ?? null,
          companyGroupName: v.companyGroupName,
          companyName: showCompanyCol
            ? (v.companyName || v.companyGroupName || "-.-"
              // || (v.cgaid != null ? `#${v.cgaid}` : '-- Superadmin --')
            )
            : undefined,
        })
      })

      setVendors(Array.from(grouped.values()))
      setListAccess({ loading: false, accessDenied: false, loadError: null })
    } catch (e: any) {
      setVendors([])
      const next = resolveAdminListFetchError(e, 'Failed to load vendors')
      setListAccess(next)
      if (!next.accessDenied) {
        toast.error('Failed to load vendors', { position: 'top-center' })
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const refresh = () => { void loadVendors() }
    window.addEventListener('rbac-permissions-updated', refresh)
    return () => window.removeEventListener('rbac-permissions-updated', refresh)
  }, [])

  useEffect(() => {
    void (async () => {
      await loadVendorCategories()
      await loadVendors()
    })()
  }, [])

  const loadVendorCategories = async () => {
    try {
      const categories = await getVendorCategories()
      setVendorCategories(categories)
    } catch (e: any) {
      console.error('Error loading vendor categories:', e)
    }
  }


  const handleSort = (key: keyof VendorRow) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }))
  }

  const filteredVendors = vendors.filter(vendor => {
    // Global filter: company
    let matchesCompany = true
    if (globalState.company) {
      const cg = globalState.company
      if (cg.type === 'company_group') {
        matchesCompany =
          Number(vendor.cgaid) === cg.id ||
          (vendor.companyMappings ?? []).some((m: any) => Number(m.cgaid) === cg.id)
      } else {
        matchesCompany =
          Number(vendor.caId) === cg.id ||
          (vendor.companyMappings ?? []).some((m: any) => Number(m.caId) === cg.id)
      }
    }
    if (!matchesCompany) return false

    return (
      (vendor.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (vendor.code || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (vendor.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (showCompanyColumn && (vendor.companyName || '').toLowerCase().includes(searchTerm.toLowerCase()))
    )
  })

  const sortedVendors = useMemo(() => {
    let sorted = [...filteredVendors]
    if (sortConfig.key !== null) {
      sorted.sort((a, b) => {
        const aVal = String((a as any)[sortConfig.key!] ?? '').toLowerCase()
        const bVal = String((b as any)[sortConfig.key!] ?? '').toLowerCase()
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1
        return 0
      })
    } else {
      sorted.sort((a, b) => b.id - a.id)
    }
    return sorted
  }, [filteredVendors, sortConfig])

  const indexOfLastVendor = currentPage * rowsPerPage
  const indexOfFirstVendor = indexOfLastVendor - rowsPerPage
  const currentVendors = sortedVendors.slice(indexOfFirstVendor, indexOfLastVendor)
  const totalPages = Math.ceil(sortedVendors.length / rowsPerPage)

  const handlePageChange = (page: number) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page)
    }
  }

  const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setRowsPerPage(parseInt(e.target.value))
    setCurrentPage(1)
  }

  const exportVendorsToPDF = () => {
    try {
      if (!sortedVendors || sortedVendors.length === 0) {
        toast.warn('No vendor data available to export.', { position: 'top-center' })
        return
      }

      const doc = new jsPDF({ orientation: 'landscape', format: 'a4' })

      // Title
      doc.setFontSize(16)
      doc.text('Vendor Management', 14, 15)

      // Subtitle with date
      doc.setFontSize(10)
      doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 22)
      doc.text(`Total Vendors: ${sortedVendors.length}`, 14, 28)

      // Prepare table data based on role
      const tableData = sortedVendors.map(vendor => {
        const categories = vendor.categoryIds.length > 0
          ? vendor.categoryIds
            .map(id => vendorCategories.find(c => c.id === id)?.name || id)
            .join(', ')
          : '-'

        const baseData = [
          vendor.code || '',
          vendor.name || '',
          vendor.email || '',
          vendor.contactNo || '',
          vendor.tag || '',
          categories,
          vendor.totalOrders?.toString() || '0',
          vendor.avgDeliveryDays?.toString() || '0',
          vendor.issueCount?.toString() || '0'
        ]

        // Add company name for superadmin (role 1)
        if (showCompanyColumn) {
          return [vendor.companyName || '', ...baseData]
        }
        return baseData
      })

      // Define table headers based on user role
      const headers = showCompanyColumn
        ? [['Company', 'Code', 'Name', 'Email', 'Contact', 'Tag', 'Categories', 'Total Orders', 'Avg Delivery Days', 'Issue Count']]
        : [['Code', 'Name', 'Email', 'Contact', 'Tag', 'Categories', 'Total Orders', 'Avg Delivery Days', 'Issue Count']]

        // Generate table
        ; (doc as any).autoTable({
          head: headers,
          body: tableData,
          startY: 35,
          styles: {
            fontSize: 7,
            cellPadding: 2,
            overflow: 'linebreak',
          },
          headStyles: {
            fillColor: [240, 240, 240],
            textColor: [0, 0, 0],
            fontStyle: 'bold',
            fontSize: 8,
          },
          alternateRowStyles: {
            fillColor: [250, 250, 250]
          },
          columnStyles: showCompanyColumn ? {
            0: { cellWidth: 30 },  // Company
            1: { cellWidth: 22 },  // Code
            2: { cellWidth: 35 },  // Name
            3: { cellWidth: 40 },  // Email
            4: { cellWidth: 28 },  // Contact
            5: { cellWidth: 20 },  // Tag
            6: { cellWidth: 35 },  // Categories
            7: { cellWidth: 22 },  // Total Orders
            8: { cellWidth: 25 },  // Avg Delivery Days
            9: { cellWidth: 20 },  // Issue Count
          } : {
            0: { cellWidth: 25 },  // Code
            1: { cellWidth: 40 },  // Name
            2: { cellWidth: 50 },  // Email
            3: { cellWidth: 32 },  // Contact
            4: { cellWidth: 22 },  // Tag
            5: { cellWidth: 45 },  // Categories
            6: { cellWidth: 25 },  // Total Orders
            7: { cellWidth: 28 },  // Avg Delivery Days
            8: { cellWidth: 22 },  // Issue Count
          },
          margin: { top: 35, left: 10, right: 10 },
          pageBreak: 'auto',
          tableLineWidth: 0.1,
          tableLineColor: [200, 200, 200],
        })

      // Save the PDF
      doc.save(`Vendor_List_${new Date().toISOString().slice(0, 10)}.pdf`)
      toast.success('PDF exported successfully!', { position: 'top-center' })
    } catch (error) {
      console.error('PDF export failed:', error)
      showErrorToast(error, 'Failed to export PDF.')
    }
  }

  const copyPasswordToClipboard = (vendorId: number, password: string) => {
    navigator.clipboard.writeText(password).then(() => {
      setCopiedPasswords(prev => new Set([...Array.from(prev), vendorId]))
      toast.success('Password copied to clipboard!', {
        position: 'top-center',
        autoClose: 2000
      })
      setTimeout(() => {
        setCopiedPasswords(prev => {
          const newSet = new Set(prev)
          newSet.delete(vendorId)
          return newSet
        })
      }, 2000)
    }).catch(() => {
      toast.error('Failed to copy password', {
        position: 'top-center'
      })
    })
  }


  return (
    <div className='app-main flex-column flex-row-fluid' id='kt_app_main'>
      <ToastContainer
        position="top-center"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        style={{ top: "5rem", zIndex: 99999 }}
      />

      <div className='d-flex flex-column flex-column-fluid'>
        <div id='kt_app_content' className='app-content flex-column-fluid bg-white'>
          <div className='card'>
            <div className='card-header border-0 pt-6 d-flex justify-content-between bg-white'>
              <div className='d-flex align-items-center gap-3'>
                <h3 className='card-label text-dark fw-bold mb-0'>Vendor Management</h3>
                {(loading || listAccess.loading) && (
                  <span className='badge badge-light-primary d-inline-flex align-items-center gap-2 py-2 px-3 fs-7 fw-normal'>
                    <span className='spinner-border spinner-border-sm text-primary' role='status' aria-hidden='true'></span>
                    Loading vendors...
                  </span>
                )}
              </div>
              <div className='card-toolbar d-flex align-items-center gap-3'>
                {canExportVendors && (
                  <button
                    type='button'
                    className='btn text-white d-inline-flex align-items-center justify-content-center'
                    style={{
                      backgroundColor: '#334155',
                      borderRadius: '9.75px',
                      padding: '8px 13px',
                      fontSize: '11.5px',
                      fontWeight: 700,
                      gap: '6.5px',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
                      transition: 'all 0.2s ease',
                      border: 'none',
                      height: '36px'
                    }}
                    onClick={exportVendorsToPDF}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                      <polyline points="7 10 12 15 17 10"/>
                      <line x1="12" y1="15" x2="12" y2="3"/>
                    </svg>
                    Export PDF
                  </button>
                )}
                {regionActionGates.canRead && (
                  <button
                    type='button'
                    className='btn text-white d-inline-flex align-items-center justify-content-center'
                    style={{
                      backgroundColor: '#7C3AED',
                      borderRadius: '9.75px',
                      padding: '8px 13px',
                      fontSize: '11.5px',
                      fontWeight: 700,
                      gap: '6.5px',
                      boxShadow: '0 2px 4px rgba(124, 58, 237, 0.2)',
                      transition: 'all 0.2s ease',
                      border: 'none',
                      height: '36px'
                    }}
                    onClick={() => setShowRegionLibraryModal(true)}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/>
                      <line x1="8" y1="2" x2="8" y2="18"/>
                      <line x1="16" y1="6" x2="16" y2="22"/>
                    </svg>
                    Region Library
                  </button>
                )}
                {actionGates.canCreate && (
                  <button
                    type='button'
                    className='btn text-white d-inline-flex align-items-center justify-content-center'
                    style={{
                      backgroundColor: '#059669',
                      borderRadius: '9.75px',
                      padding: '8px 13px',
                      fontSize: '11.5px',
                      fontWeight: 700,
                      gap: '6.5px',
                      boxShadow: '0 2px 4px rgba(5,150,105,0.2)',
                      transition: 'all 0.2s ease',
                      border: 'none',
                      height: '36px'
                    }}
                    onClick={() => setShowBulkUploadModal(true)}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                      <polyline points="17 8 12 3 7 8"/>
                      <line x1="12" y1="3" x2="12" y2="15"/>
                    </svg>
                    Bulk Upload
                  </button>
                )}
                {actionGates.canCreate && (
                  <button
                    type='button'
                    className='btn btn-primary text-white d-inline-flex align-items-center justify-content-center'
                    style={{
                      borderRadius: '9.75px',
                      padding: '8px 13px',
                      fontSize: '11.5px',
                      fontWeight: 700,
                      gap: '6.5px',
                      boxShadow: '0 2px 4px rgba(37,99,235,0.2)',
                      transition: 'all 0.2s ease',
                      border: 'none',
                      height: '36px'
                    }}
                    onClick={() => {
                      setEditingVendor(null)
                      setShowAddVendorModal(true)
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="12" y1="5" x2="12" y2="19" />
                      <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                    Add Vendor
                  </button>
                )}
              </div>
            </div>

            <div className='card-body py-4 bg-white border-top'>
              {/* Notice / Error Banner */}
              {listAccess.loadError && (
                <div className='alert alert-danger d-flex align-items-center justify-content-between p-3 mb-4 rounded-3 border border-danger-subtle'>
                  <div className='d-flex align-items-center gap-3'>
                    <i className='bi bi-exclamation-triangle-fill fs-3 text-danger' />
                    <div>
                      <div className='fw-bold text-danger fs-7'>Failed to Load Vendors</div>
                      <div className='text-dark fs-8'>{listAccess.loadError}</div>
                    </div>
                  </div>
                  <button
                    className='btn btn-sm btn-outline-danger d-flex align-items-center gap-1 px-3 py-1 text-nowrap'
                    onClick={loadVendors}
                    disabled={loading || listAccess.loading}
                  >
                    <i className={`bi bi-arrow-clockwise ${(loading || listAccess.loading) ? 'spinner-border spinner-border-sm' : ''}`} />
                    {(loading || listAccess.loading) ? 'Retrying...' : 'Retry'}
                  </button>
                </div>
              )}
              {listAccess.accessDenied && (
                <div className='alert alert-warning d-flex align-items-center p-3 mb-4 rounded-3 border border-warning-subtle'>
                  <i className='bi bi-shield-lock-fill fs-3 text-warning me-3' />
                  <div>
                    <div className='fw-bold text-warning fs-7'>Access Restricted</div>
                    <div className='text-muted fs-8'>You do not have permission to view vendors. Contact your administrator if you need access.</div>
                  </div>
                </div>
              )}

              <div className='row g-3 align-items-end mb-4'>
                <div className='col-md-6'>
                  <label className='form-label text-muted fw-semibold fs-7 mb-2'>Search</label>
                  <div className='position-relative'>
                    <div
                      className='position-absolute ms-3'
                      style={{ top: '50%', transform: 'translateY(-50%)' }}
                    >
                      <KTSVG
                        path='/media/icons/duotune/general/gen021.svg'
                        className='svg-icon-2'
                      />
                    </div>
                    <input
                      type='text'
                      className='form-control form-control-sm ps-10'
                      placeholder='Search vendors...'
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
              </div>


              <div className='report-table table-responsive'>
                <table className='table table-bordered align-middle'>
                  <thead className='table-header text-start'>
                    <tr>
                      <th style={{ width: '90px' }}>SR/NO</th>
                      <th onClick={() => handleSort('code')} className='cursor-pointer'>
                        <div className='d-flex align-items-center'>
                          CODE
                          <div style={{ transform: 'translateY(-2px)' }}>
                            <KTSVG
                              path={`/media/map/sort-col-${sortConfig.key === 'code'
                                ? sortConfig.direction === 'asc' ? 'up-black' : 'down-black'
                                : 'grey'
                                }.svg`}
                              className='svg-icon ms-2 custom-sort-icon'
                            />
                          </div>
                        </div>
                      </th>
                      <th onClick={() => handleSort('name')} className='cursor-pointer'>
                        <div className='d-flex align-items-center'>
                          NAME
                          <div style={{ transform: 'translateY(-2px)' }}>
                            <KTSVG
                              path={`/media/map/sort-col-${sortConfig.key === 'name'
                                ? sortConfig.direction === 'asc' ? 'up-black' : 'down-black'
                                : 'grey'
                                }.svg`}
                              className='svg-icon ms-2 custom-sort-icon'
                            />
                          </div>
                        </div>
                      </th>
                      {showCompanyColumn && (
                        <th onClick={() => handleSort('companyName' as keyof VendorRow)} className='cursor-pointer'>
                          <div className='d-flex align-items-center'>
                            COMPANY
                            <div style={{ transform: 'translateY(-2px)' }}>
                              <KTSVG
                                path={`/media/map/sort-col-${sortConfig.key === 'companyName'
                                  ? sortConfig.direction === 'asc' ? 'up-black' : 'down-black'
                                  : 'grey'
                                  }.svg`}
                                className='svg-icon ms-2 custom-sort-icon'
                              />
                            </div>
                          </div>
                        </th>
                      )}
                      <th onClick={() => handleSort('email')} className='cursor-pointer'>EMAIL</th>
                      <th onClick={() => handleSort('contactNo')} className='cursor-pointer'>CONTACT</th>
                      <th>TAG</th>
                      <th>CATEGORIES</th>
                      <th>TOTAL ORDERS</th>
                      <th>AVG DELIVERY DAYS</th>
                      <th>ISSUE COUNT</th>
                      <th>ACTIONS</th>
                    </tr>
                  </thead>


                  <tbody className='table-body text-start'>
                    {(loading || listAccess.loading) ? (
                      Array.from({ length: 8 }).map((_, i) => (
                        <tr key={`vendor-sk-${i}`}>
                          <td className='py-3' style={{ width: '90px' }}>
                            <div className='placeholder-glow'>
                              <span className='placeholder col-6 rounded' style={{ height: 16 }}></span>
                            </div>
                          </td>
                          <td className='py-3'>
                            <div className='placeholder-glow'>
                              <span className='placeholder col-7 rounded' style={{ height: 16 }}></span>
                            </div>
                          </td>
                          <td className='py-3'>
                            <div className='placeholder-glow'>
                              <span className='placeholder col-8 rounded' style={{ height: 16 }}></span>
                            </div>
                          </td>
                          {showCompanyColumn && (
                            <td className='py-3'>
                              <div className='placeholder-glow'>
                                <span className='placeholder col-8 rounded-pill' style={{ height: 18 }}></span>
                              </div>
                            </td>
                          )}
                          <td className='py-3'>
                            <div className='placeholder-glow'>
                              <span className='placeholder col-9 rounded' style={{ height: 16 }}></span>
                            </div>
                          </td>
                          <td className='py-3'>
                            <div className='placeholder-glow'>
                              <span className='placeholder col-7 rounded' style={{ height: 16 }}></span>
                            </div>
                          </td>
                          <td className='py-3'>
                            <div className='placeholder-glow'>
                              <span className='placeholder col-6 rounded-pill' style={{ height: 18 }}></span>
                            </div>
                          </td>
                          <td className='py-3'>
                            <div className='placeholder-glow'>
                              <span className='placeholder col-8 rounded' style={{ height: 16 }}></span>
                            </div>
                          </td>
                          <td className='py-3'>
                            <div className='placeholder-glow'>
                              <span className='placeholder col-5 rounded' style={{ height: 16 }}></span>
                            </div>
                          </td>
                          <td className='py-3'>
                            <div className='placeholder-glow'>
                              <span className='placeholder col-5 rounded' style={{ height: 16 }}></span>
                            </div>
                          </td>
                          <td className='py-3'>
                            <div className='placeholder-glow'>
                              <span className='placeholder col-5 rounded' style={{ height: 16 }}></span>
                            </div>
                          </td>
                          <td className='py-3'>
                            <div className='placeholder-glow'>
                              <span className='placeholder col-8 rounded' style={{ height: 24 }}></span>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : currentVendors.length === 0 ? (
                      <tr>
                        <td colSpan={showCompanyColumn ? 12 : 11} className='text-center text-muted py-5'>
                          {adminListEmptyMessage(listAccess, 'No vendors found.')}
                        </td>
                      </tr>
                    ) : (
                      currentVendors.map((vendor, idx) => (
                        <tr key={vendor.id}>
                          <td className='text-dark fs-6'>{indexOfFirstVendor + idx + 1}</td>
                          <td className='text-dark fw-semibold fs-6'>
                            {vendor.code}
                            {vendor.vendorInitials ? <span className='badge badge-light-info ms-2'>{vendor.vendorInitials}</span> : null}
                          </td>
                          <td className='text-dark fw-bold fs-6'>{vendor.name}</td>
                          {showCompanyColumn && (
                            <td className='text-dark fs-6'>
                              {vendor.companyName === '-- Superadmin --' ? (
                                <span className='badge rounded-pill bg-light text-muted border'>
                                  -- Superadmin --
                                </span>
                              ) : (
                                vendor.companyName || '-'
                              )}
                            </td>
                          )}
                          <td className='text-dark fs-6'>{vendor.email}</td>
                          <td className='text-dark fs-6'>{vendor.contactNo}</td>
                          <td className='text-dark fs-6'>
                            <span className={`badge ${vendor.tag === 'NORMAL' ? 'bg-success' : 'bg-warning'}`}>
                              {vendor.tag}
                            </span>
                          </td>
                          <td className='text-dark fs-6'>
                            {vendor.categoryIds.length > 0
                              ? vendor.categoryIds
                                .map(id => vendorCategories.find(c => c.id === id)?.name || id)
                                .join(', ')
                              : '-'}
                          </td>
                          <td className='text-dark fs-6'>{vendor.totalOrders}</td>
                          <td className='text-dark fs-6'>{vendor.avgDeliveryDays}</td>
                          <td className='text-dark fs-6'>{vendor.issueCount}</td>
                          <td className='text-dark fs-6'>
                            <div className='d-flex align-items-center'>
                              {actionGates.canRead && (
                                <button
                                  type='button'
                                  className='btn btn-sm btn-light-info me-2'
                                  title='View vendor performance'
                                  aria-label={`View performance for ${vendor.name}`}
                                  onClick={async () => {
                                    try { setPerformanceVendor(await getVendorDetails(vendor.id)) }
                                    catch (err) { showErrorToast(err, 'Failed to load vendor details.') }
                                  }}
                                >
                                  <i className='bi bi-eye' />
                                </button>
                              )}
                              {actionGates.canUpdate && (
                                <button
                                  type='button'
                                  className='btn btn-sm btn-light-primary'
                                  onClick={async () => {
                                    try {
                                      const details = await getVendorDetails(vendor.id)
                                      setEditingVendor(details)
                                      setShowAddVendorModal(true)
                                    } catch (err) {
                                      console.error('Failed to load vendor details:', err)
                                      showErrorToast(err, 'Failed to load vendor details.')
                                    }
                                  }}
                                >
                                  Edit
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>


                <div className='pagination-wrapper d-flex justify-content-between align-items-center py-3'>
                  <div className='d-flex align-items-center'>
                    <span className='text-muted me-2'>Rows per page</span>
                    <SearchableSelect
                      className='form-select form-select-sm'
                      style={{
                        borderRadius: '20px',
                        width: '70px',
                        border: '1px solid #dee2e6',
                        fontSize: '14px',
                        padding: '4px 8px'
                      }}
                      value={rowsPerPage}
                      onChange={handleRowsPerPageChange}
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </SearchableSelect>
                  </div>
                  <div className='d-flex align-items-center'>
                    <span className='text-muted me-3' style={{ fontSize: '14px' }}>
                      Showing <strong>{((currentPage - 1) * rowsPerPage) + 1}-{Math.min(currentPage * rowsPerPage, sortedVendors.length)}</strong> of <strong>{sortedVendors.length}</strong>
                    </span>

                    <nav>
                      <ul className='pagination pagination-sm mb-0' style={{ gap: '2px' }}>
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                          <button
                            className='page-link text-muted'
                            style={{
                              backgroundColor: '#f8f9fa',
                              border: '1px solid #dee2e6',
                              padding: '8px 12px',
                              fontSize: '14px',
                              borderRadius: '6px'
                            }}
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                          >
                            ‹
                          </button>
                        </li>


                        {(() => {
                          const pages = []
                          const showPages = 5
                          let startPage = Math.max(1, currentPage - 2)
                          let endPage = Math.min(totalPages, startPage + showPages - 1)

                          if (endPage - startPage < showPages - 1) {
                            startPage = Math.max(1, endPage - showPages + 1)
                          }

                          for (let i = startPage; i <= endPage; i++) {
                            pages.push(
                              <li key={i} className={`page-item ${currentPage === i ? 'active' : ''}`}>
                                <button
                                  className='page-link text-muted'
                                  style={{
                                    backgroundColor: currentPage === i ? '#F4F9FF' : 'transparent',
                                    border: '1px solid #dee2e6',
                                    padding: '8px 12px',
                                    fontSize: '14px',
                                    minWidth: '40px',
                                    borderRadius: '6px'
                                  }}
                                  onClick={() => handlePageChange(i)}
                                >
                                  {i}
                                </button>
                              </li>
                            )
                          }
                          return pages
                        })()}

                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                          <button
                            className='page-link text-muted'
                            style={{
                              backgroundColor: '#f8f9fa',
                              border: '1px solid #dee2e6',
                              padding: '8px 12px',
                              fontSize: '14px',
                              borderRadius: '6px'
                            }}
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                          >
                            ›
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>


      <AddVendorModal
        visible={showAddVendorModal}
        vendor={editingVendor}
        vendorCategories={vendorCategories}
        onClose={() => {
          setShowAddVendorModal(false)
          setEditingVendor(null)
        }}
        onSubmit={() => {
          loadVendors()
          toast.success(
            editingVendor ? 'Vendor updated successfully!' : 'Vendor created successfully!',
            { position: 'top-center' }
          )
        }}
      />
      <BulkVendorUploadModal
        visible={showBulkUploadModal}
        onClose={() => setShowBulkUploadModal(false)}
        onSuccess={() => {
          loadVendors()
        }}
      />
      <RegionLibraryModal visible={showRegionLibraryModal} onClose={() => setShowRegionLibraryModal(false)} />
      <VendorPerformanceModal
        visible={performanceVendor !== null}
        vendor={performanceVendor}
        onClose={() => setPerformanceVendor(null)}
      />
    </div>
  )
}

export default VendorManagement
